import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import ContactsService, { type Contact } from '../services/contacts'
import { unknownNumberApi, type PhoneHistoryCall } from '../services/phoneHistory'
import type { DonationLookup } from '../services/allodons'
import { retryUnlessRateLimited } from '../utils/queryRetry'
import { useIntegration } from './useIntegrations'

/**
 * LOT-NUMÉRO-INCONNU — ce que le CRM sait d'un numéro cliqué dans le journal
 * des appels, la liste « À rappeler » ou les enregistrements.
 *
 * Demande du client (2026-10-13) : « les numéros appelés mais non enregistrés
 * dans le CRM ont-ils un historique comme les contacts ? » — jusqu'ici, non.
 *
 * Trois lectures, dans cet ordre :
 *   ① la FICHE (`GET /contacts/find-by-phone`) — si le numéro en a finalement
 *      une, le panneau ne montre qu'« Ouvrir la fiche » : rien d'autre ne part ;
 *   ② sinon l'HISTORIQUE des appels du tenant pour ce numéro ;
 *   ③ et ses DONS AlloDons — section masquée dès que la lecture échoue (tenant
 *      sans raccordement : 404) ou qu'il n'y a rien à montrer. Le serveur rend
 *      un résultat VIDE (pas un 404) à un tenant sans AlloDons : « zéro don »
 *      et « pas d'AlloDons » sont donc indiscernables ici, et tous deux masqués.
 *
 * #270 : AlloDons est OPTIONNEL — la lecture ③ ne part QUE si l'organisation
 * l'a raccordé (`useIntegration('allodons')`, faux pendant la lecture de
 * l'état) ; sans raccordement, toute mention de dons reste masquée et le
 * serveur n'est même pas interrogé (il répondrait 404
 * `ALLODONS_NOT_CONNECTED`).
 *
 * Le hook ne tourne qu'au MONTAGE du panneau (`enabled`) : jamais au rendu
 * d'une liste — un journal de 200 lignes ne doit pas tirer 600 requêtes.
 * `staleTime` court : l'historique d'un numéro change pendant une session.
 */

export const UNKNOWN_NUMBER_QUERY_KEY = ['unknown-number'] as const
export const UNKNOWN_NUMBER_STALE_MS = 15_000

export interface UseUnknownNumberResult {
  phone: string
  /** `undefined` tant que la fiche n'est pas lue ; `null` = aucune fiche. */
  contact: Contact | null | undefined
  contactLoading: boolean
  history: PhoneHistoryCall[] | undefined
  historyLoading: boolean
  historyError: boolean
  retryHistory: () => void
  /** `null` = rien à montrer (échec, pas de raccordement, zéro don). */
  donations: DonationLookup | null
  donationsLoading: boolean
  /** Vrai dès que la section Dons ne doit pas être rendue. */
  donationsHidden: boolean
  /** Après la création de la fiche : la relit (le panneau bascule sur « Ouvrir la fiche »). */
  invalidateContact: () => void
}

export function useUnknownNumber(
  phone: string,
  options: { enabled?: boolean } = {},
): UseUnknownNumberResult {
  const enabled = options.enabled !== false
  const queryClient = useQueryClient()
  const contactKey = [...UNKNOWN_NUMBER_QUERY_KEY, phone, 'contact'] as const

  // ① La fiche. `findByPhone` rend déjà `null` sur 404 (et sur toute erreur).
  const contactQuery = useQuery({
    queryKey: contactKey,
    queryFn: () => ContactsService.findByPhone(phone),
    enabled,
    staleTime: UNKNOWN_NUMBER_STALE_MS,
  })
  const unknown = enabled && contactQuery.isSuccess && contactQuery.data === null
  const allodons = useIntegration('allodons')
  const donationsEnabled = unknown && allodons.enabled

  // ② L'historique — seulement pour un numéro sans fiche.
  const historyQuery = useQuery({
    queryKey: [...UNKNOWN_NUMBER_QUERY_KEY, phone, 'history'],
    queryFn: () => unknownNumberApi.history(phone),
    enabled: unknown,
    staleTime: UNKNOWN_NUMBER_STALE_MS,
    retry: retryUnlessRateLimited,
  })

  // ③ Les dons — le client rend `null` sur tout échec, donc jamais d'erreur ici.
  const donationsQuery = useQuery({
    queryKey: [...UNKNOWN_NUMBER_QUERY_KEY, phone, 'donations'],
    queryFn: () => unknownNumberApi.donations(phone),
    enabled: donationsEnabled,
    staleTime: UNKNOWN_NUMBER_STALE_MS,
    retry: false,
  })

  const donations = donationsQuery.data ?? null
  const donationsHidden = !allodons.enabled || !donations || donations.summary.donationCount === 0

  const retryHistory = useCallback(() => {
    void historyQuery.refetch()
  }, [historyQuery])

  const invalidateContact = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: contactKey })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, phone])

  return {
    phone,
    contact: contactQuery.data,
    contactLoading: enabled && contactQuery.isPending,
    history: historyQuery.data,
    historyLoading: unknown && historyQuery.isPending,
    historyError: historyQuery.isError,
    retryHistory,
    donations,
    donationsLoading: donationsEnabled && donationsQuery.isPending,
    donationsHidden,
    invalidateContact,
  }
}

export default useUnknownNumber
